import React from "react";
import { Col } from "react-bootstrap";
import Input from "components/Form/input";
import ReactSelect from "components/Form/react-select";

const FieldDetails = ({
    control, // Field configuration (e.g., { type: "text", label: "Name", fieldName: "name", ... })
    updateFormData, // Function to update form data
    formData, // Entire form data as an object
    fieldKey, // Key of this field in formData (e.g., "name")
    register, // Form registration function
    errors, // Form errors
    getValues, // Function to get form values
}) => {
    // Handle input changes
    const handleChange = (key, value) => {
        const updatedFields = { ...formData };
        updatedFields[fieldKey] = { ...control, [key]: value };
        updateFormData(updatedFields);
    };

    // Handle field removal
    const handleRemove = () => {
        const updatedFields = { ...formData };
        delete updatedFields[fieldKey];
        updateFormData(updatedFields);
    };

    // Options for select inputs
    const checkedOptions = [
        { value: "unchecked", label: "unchecked" },
        { value: "checked", label: "checked" },
    ];
    const dropdownOptions = [
        { value: "none", label: "none" },
        { value: "first-option", label: "first option" },
    ];
    const tagOptions = ["h1", "h2", "h3", "h4", "h5", "h6"].map((tag) => ({
        value: tag,
        label: tag,
    }));

    // Determine default value input based on field type
    let defaultElement;

    // Helper to get selected value for ReactSelect
    const getSelected = (value, options) =>
        options.find((option) => option.value === value) || options[0];

    if (["checkbox", "radio"].includes(control?.type)) {
        defaultElement = (
            <ReactSelect
                name={`fields.${fieldKey}.defaultChecked`}
                options={checkedOptions}
                value={getSelected(
                    control?.defaultChecked || "unchecked",
                    checkedOptions
                )}
                onChange={(option) =>
                    handleChange("defaultChecked", option.value)
                }
                register={register}
                errors={errors}
                getValues={getValues}
            />
        );
    } else if (control?.type === "dropdown") {
        defaultElement = (
            <ReactSelect
                name={`fields.${fieldKey}.defaultSelected`}
                options={dropdownOptions}
                value={getSelected(
                    control?.defaultSelected || "none",
                    dropdownOptions
                )}
                onChange={(option) =>
                    handleChange("defaultSelected", option.value)
                }
                register={register}
                errors={errors}
                getValues={getValues}
            />
        );
    } else {
        defaultElement = (
            <Input
                name={`fields.${fieldKey}.defaultValue`}
                type={control?.type === "image" ? "text" : control?.type}
                value={control?.defaultValue || ""}
                onChange={(event) => handleChange("defaultValue", event.target.value)}
                register={register}
                errors={errors}
                getValues={getValues}
            />
        );
    }
    return (
        <div className="d-flex flex-wrap">
            <Col xs="12">
                {/* Field Label */}
                <Input
                    name={`fields.${fieldKey}.label`}
                    type="text"
                    value={control?.label || ""}
                    onChange={(event) => handleChange("label", event.target.value)}
                    register={register}
                    errors={errors}
                    getValues={getValues}
                    langId="label"
                    inputClass="title-input"
                />
                <div className="d-flex gap-2">
                    {/* Required Checkbox */}
                    <Input
                        name={`fields.${fieldKey}.required`}
                        type="checkbox"
                        checked={control?.required || false}
                        onChange={(event) =>
                            handleChange("required", event.target.checked)
                        }
                        register={register}
                        errors={errors}
                        getValues={getValues}
                        langId="required"
                    />
                    <span className="remove" onClick={handleRemove}>
                        x
                    </span>
                </div>
            </Col>

            {/* Form Inputs */}
            <div className="form-wrapper">
                {/* Default Value */}
                {defaultElement}

                {/* Field Name */}
                <Input
                    name={`fields.${fieldKey}.fieldName`}
                    type="text"
                    value={control?.fieldName || ""}
                    onChange={(event) => handleChange("fieldName", event.target.value)}
                    register={register}
                    errors={errors}
                    getValues={getValues}
                    langId="fieldName"
                />

                {/* Placeholder */}
                <Input
                    name={`fields.${fieldKey}.placeholder`}
                    type="text"
                    value={control?.placeholder || ""}
                    onChange={(event) =>
                        handleChange("placeholder", event.target.value)
                    }
                    register={register}
                    errors={errors}
                    getValues={getValues}
                    langId="placeholder"
                />

                {/* Type-Specific Inputs */}
                {control?.type === "title" && (
                    <ReactSelect
                        name={`fields.${fieldKey}.tag`}
                        options={tagOptions}
                        value={getSelected(control?.tag || "h1", tagOptions)}
                        onChange={(option) => handleChange("tag", option.value)}
                        register={register}
                        errors={errors}
                        getValues={getValues}
                    />
                )}

                {control?.type === "file" && (
                    <>
                        <Input
                            name={`fields.${fieldKey}.accept`}
                            type="text"
                            value={control?.accept || ""}
                            onChange={(event) =>
                                handleChange("accept", event.target.value)
                            }
                            register={register}
                            errors={errors}
                            getValues={getValues}
                            langId="accept"
                            list="file-accept"
                        />
                        <datalist id="file-accept">
                            {[
                                "image/*",
                                "video/*",
                                "audio/*",
                                ".csv",
                                ".pdf",
                            ].map((type) => (
                                <option key={type} value={type} />
                            ))}
                        </datalist>
                    </>
                )}

                {control?.type === "textarea" && (
                    <>
                        <Input
                            name={`fields.${fieldKey}.col`}
                            type="number"
                            value={control?.col || ""}
                            onChange={(event) =>
                                handleChange("col", event.target.value)
                            }
                            register={register}
                            errors={errors}
                            getValues={getValues}
                            langId="col"
                        />
                        <Input
                            name={`fields.${fieldKey}.rows`}
                            type="number"
                            value={control?.rows || ""}
                            onChange={(event) =>
                                handleChange("rows", event.target.value)
                            }
                            register={register}
                            errors={errors}
                            getValues={getValues}
                            langId="rows"
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default FieldDetails;
